import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, UrlTree } from '@angular/router';
import { AppLicense } from '@core/licenses';
import { Nullable } from '@pin-up-global/common';
import { decodeJwt } from '@shared/utils';
import { NgxPermissionsService } from 'ngx-permissions';
import {
  BehaviorSubject, filter, finalize, merge, Observable, of, Subject, switchMap, take, takeUntil,
  takeWhile, tap, throwError,
} from 'rxjs';
import { AuthUtilityService } from './auth-utility/auth-utility.service';
import { AccessData } from './auth.abstraction';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  #router = inject(Router);
  #permissionsService = inject(NgxPermissionsService);
  #authUtilityService = inject(AuthUtilityService);
  #matDialog = inject(MatDialog);

  #isAuthorized$ = new BehaviorSubject(false);
  isAuthorized$ = this.#isAuthorized$.asObservable();
  #isTokenRefreshing$ = new BehaviorSubject(false);
  #isTokenRefreshingError$ = new Subject<void>();
  #tokenRefreshingError$ = this.#isTokenRefreshingError$.pipe(
      switchMap((error) => throwError(() => error)),
  );

  refresh(): Observable<Nullable<Omit<AccessData, 'refresh'>>> {
    return this.#isTokenRefreshing$.pipe(
        take(1),
        switchMap(isRefreshing => {
              if (isRefreshing) {
                return merge(
                    this.#isTokenRefreshing$,
                    this.#tokenRefreshingError$,
                ).pipe(
                    takeUntil(this.#tokenRefreshingError$),
                    takeWhile(Boolean, true),
                    filter(value => !value),
                    switchMap(() => of(null)),
                );
              }

              this.#isTokenRefreshing$.next(true);
              return this.#authUtilityService.refresh().pipe(
                  finalize(() => this.#isTokenRefreshing$.next(false)),
                  tap({
                    next: accessData => this.updateAccessData(accessData),
                    error: (error) => this.#isTokenRefreshingError$.next(error),
                  }),
              );
            },
        ),
    );
  }

  getSignInUrlTree(): UrlTree {
    return this.#authUtilityService.getSignInUrlTree();
  }

  getPostSignInUrlTree(): UrlTree {
    return this.#authUtilityService.getPostSignInUrlTree();
  }

  getAccessToken(): string | null {
    return this.#authUtilityService.getAccessToken();
  }

  getRefreshToken(): string | null {
    return this.#authUtilityService.getRefreshToken();
  }

  removeTokens(): void {
    this.#authUtilityService.removeTokens();
  }

  logout(): void {
    this.removeTokens();
    this.#matDialog.closeAll();
    this.#permissionsService.flushPermissions();
    this.#isAuthorized$.next(false);

    this.#router.navigateByUrl(this.getSignInUrlTree());
  }

  updateAccessData(accessData: Partial<AccessData>): void {
    if (accessData.permissions) {
      this.#savePermissions(accessData.permissions);
    }

    if (accessData.access) {
      this.#authUtilityService.saveTokens(accessData.access, accessData.refresh);
    }

    this.#isAuthorized$.next(true);
  }

  get allAvailableLicenses(): AppLicense[] {
    const accessToken = decodeJwt<{ groups: string[] }>(
      this.getAccessToken() ?? '',
      window,
    );
    const licenses = (accessToken.groups || [])
      .map(group => group.split('.')[1]);

    if (licenses.includes('dev')) {
      return [AppLicense.KZ];
    }

    return licenses as AppLicense[];
  }

  #savePermissions(permissions: string[]): void {
    this.#permissionsService.loadPermissions(permissions);
  }
}
